import { zodResolver } from '@hookform/resolvers/zod';
import { ApplyForm } from 'api/U002';
import { AnswerFileType, AuditionFormValues } from 'commons/auditionForm';
import { getAnswers, setAnswers } from 'commons/auditionForm/autosave/utils';
import { AuditionFormRouteContext } from 'pages/AuditionFormTop';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AppContext } from 'templates/AppContextProvider';
import { z } from 'zod';
import { Consts } from 'commons/ui-generator/ContentsHandler';

interface Answer {
  apply_form_no: string;
  answer: string;
  answer_etc: string;
  answer_kbn: string;
}

export const MembersAnswerSchema = z
  .object({
    input1: z.string().optional(),
    input2: z.string().optional(),
    input3: z.string().optional(),
    input4: z.string().optional(),
    input5: z.string().optional(),
    input6: z.string().optional(),
    apply_type: z.string().optional(),
    member_num: z.coerce.number().int().max(20).optional(),
    consent: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.apply_type === undefined) return;

    // ソロ選択の場合メンバー数の最小値を0としてバリデーションに引っかからないようにする
    const isSolo = val.apply_type === 'Solo' || val.apply_type === 'ソロ';
    const min = isSolo ? 0 : 2;

    if (!isSolo) {
      if (!val.member_num || (val.member_num && val.member_num < min)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['member_num'],
          message: `${min}以上で入力してください`,
        });
      }
    }
  });
export type MembersAnswerType = typeof MembersAnswerSchema;
export type MembersAnswerInput = z.infer<typeof MembersAnswerSchema>;
const MembersAnswersSchema = MembersAnswerSchema.array().optional();
type MembersAnswersSchemaType = typeof MembersAnswersSchema;
export type MembersAnswersInput = z.infer<typeof MembersAnswersSchema>;

const FileInputSchema = z.instanceof(FileList).optional();
type FileInputSchemaType = typeof FileInputSchema;
const CheckBoxSchema = z
  .object({
    labelAndValues: z
      .preprocess((v) => (v === false ? [] : typeof v === 'string' ? [v] : v), z.string().array())
      .optional(),
    etc: z.string().optional(),
  })
  .optional();
type CheckBoxSchemaType = z.infer<typeof CheckBoxSchema>;
const RadioSchema = z
  .object({
    labelAndValues: z.preprocess((v) => (v === null ? '' : v), z.string()).optional(),
    etc: z.string().optional(),
  })
  .optional();
type RadioSchemaType = z.infer<typeof RadioSchema>;
const SelectSchema = z.string().optional();
type SelectSchemaType = typeof SelectSchema;
const DateSelectSchema = z.string().array().optional();
type DateSelectSchemaType = typeof DateSelectSchema;
const BasicSchema = z
  .string()
  .refine((val) => !val || Consts.NONE_REGEX.test(val), '絵文字等は利用できません。')
  .optional();
type BasicSchemaType = typeof BasicSchema;
export const CategorySchema = z
  .object({
    labelAndValues: z
      .preprocess((v) => (v === false ? [] : typeof v === 'string' ? [v] : v), z.string().array())
      .optional(),
    etc: z.string().optional(),
  })
  .optional();
export type CategoryInputs = z.infer<typeof CategorySchema>;

// export type AnswerSchemaFormats = '08' | '09' | '03' | '04' | '06' | '54';
// export type AnswerSchemaType<T extends AnswerSchemaFormats> =
//   T extends '08' | '09' | '17'
//   ? FileInputSchemaType
//   : T extends '03' | '04'
//   ? SelectSchemaType
//   : T extends '06'
//   ? SelectSchemaType
//   : T extends '54'
//   ? ConsentAnswersSchemaType
//   : BasicSchemaType;
export type AnswerSchemaType = ReturnType<typeof createAnswerSchema>;
export const createAnswerSchema = (form: ApplyForm, isGlobal: boolean) => {
  const { format } = form;
  if ((format === '12' && !isGlobal) || format === '52') {
    return SelectSchema;
  }
  if (format === '08' || format === '09' || format === '17') {
    return FileInputSchema;
  }
  if (format === '03' || format === '58') {
    return CheckBoxSchema;
  }
  if (format === '04' || format === '16') {
    return RadioSchema;
  }
  if (format === '06') {
    return DateSelectSchema;
  }
  if (format === '20') {
    return FileInputSchema;
  }
  if (format === '54') {
    return MembersAnswersSchema;
  }
  if (format === '57') {
    return CategorySchema;
  }
  return BasicSchema;
};

export type AuditionFormValuesSchema = ReturnType<typeof createAuditionFormValuesSchema>;
export const createAuditionFormValuesSchema = (applyForms: ApplyForm[], isGlobal: boolean) =>
  z.object(
    applyForms.reduce<Record<`${number}`, AnswerSchemaType>>((acc, form) => {
      const { apply_form_no } = form;
      const schema = createAnswerSchema(form, isGlobal);
      return {
        ...acc,
        [`${apply_form_no}`]: schema,
      };
    }, {})
  );
export type AuditionFormValuesSchemaInput = z.input<AuditionFormValuesSchema>;

/**
 * AuditionFormValuesSchemaInputをAuditionFormValuesに変換する
 */
export const transformAuditionFormInputToAuditionFormValues = (
  applyForms: ApplyForm[],
  prefectures: { value: string; label: string }[],
  preferred_venue_list: { value: string; label: string }[],
  values: AuditionFormValuesSchemaInput,
  isGlobal: boolean,
  setHasErrorInSelectDate?: React.Dispatch<React.SetStateAction<boolean>>,
  setHasErrorInMemberConsent?: React.Dispatch<React.SetStateAction<boolean>>,
  setHasErrorConsentFiles?: React.Dispatch<React.SetStateAction<boolean>>
): AuditionFormValues => {
  return applyForms.map((form) => {
    const apply_form_no = `${form.apply_form_no}` as const;
    const format = form.format;

    if (values[apply_form_no] === undefined) {
      return {
        apply_form_no: apply_form_no,
        answer: '',
        answer_etc: '',
        answer_kbn: '',
      };
    }

    const answer_etc = '';
    // 52：希望会場 58:参加可能会場
    if (['52', '58'].includes(format)) {
      const data = values[apply_form_no];
      const pd_apply_form_no = applyForms.find(
        (applyForm) => applyForm.format === '58'
      )?.apply_form_no;
      let possibilityData: CheckBoxSchemaType = undefined;
      if (pd_apply_form_no) {
        possibilityData = values[`${pd_apply_form_no}` as const] as unknown as CheckBoxSchemaType;
      }
      const possibilityDataLabelAndValues = Array.isArray(possibilityData?.labelAndValues)
        ? possibilityData?.labelAndValues
        : possibilityData?.labelAndValues === false
          ? []
          : [possibilityData?.labelAndValues];
      // 参加可能会場の回答情報
      if (format === '58' && data != null) {
        const answer = possibilityDataLabelAndValues.map((labelAndValue) => {
          const possibilityAnswer =
            preferred_venue_list.find(({ value }) => value === labelAndValue)?.label ?? '';
          return possibilityAnswer;
        });
        const answer_kbn = possibilityDataLabelAndValues.join(',');
        return {
          apply_form_no: apply_form_no,
          answer: answer ? answer.join('\n') : '',
          answer_etc: '',
          answer_kbn: answer_kbn ?? '',
        };
      }
      if (data == null || typeof data !== 'string') {
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const selectedValue = data;
      const preferredAnswer =
        preferred_venue_list.find(({ value }) => value === selectedValue)?.label ?? '';
      const possibilityAnswers = possibilityDataLabelAndValues.map((labelAndValue) => {
        const possibilityAnswer =
          preferred_venue_list.find(({ value }) => value === labelAndValue)?.label ?? '';
        return possibilityAnswer;
      });

      const answer =
        (preferredAnswer === '' ? '希望会場選択なし' : preferredAnswer) +
        (possibilityAnswers ? '\n' + possibilityAnswers?.join('\n') : '');
      const answer_kbn = selectedValue;
      return {
        apply_form_no: apply_form_no,
        answer,
        answer_etc,
        answer_kbn,
      };
    }
    // 12：都道府県:国内
    if (format === '12' && !isGlobal) {
      const data = values[apply_form_no];
      if (data == null || typeof data !== 'string') {
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const selectedValue = data;
      const answer = prefectures.find(({ value }) => value === selectedValue)?.label ?? '';
      const answer_kbn = selectedValue;
      return {
        apply_form_no: apply_form_no,
        answer,
        answer_etc,
        answer_kbn,
      };
    }
    // 12: 都道府県:海外
    if (format === '12' && isGlobal) {
      const data = values[apply_form_no];
      if (data == null || typeof data !== 'string') {
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      return {
        apply_form_no: apply_form_no,
        answer: data,
        answer_etc,
        answer_kbn: null,
      };
    }

    // 03: リストボックス(チェックボックス)
    if (['03'].includes(format)) {
      const datas = values[apply_form_no] as unknown as CheckBoxSchemaType;
      if (
        !datas ||
        !Array.isArray(datas?.labelAndValues) ||
        datas.labelAndValues.length === 0 ||
        typeof datas.labelAndValues[0] !== 'string'
      ) {
        if (datas?.etc) {
          return {
            apply_form_no: apply_form_no,
            answer: '',
            answer_etc: datas.etc,
            answer_kbn: '',
          };
        }
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const selectedLabelAndValues = (datas.labelAndValues as unknown as string[]).map(
        (data) => JSON.parse(data.toString()) as { value: string; label: string }
      );

      const answer = selectedLabelAndValues
        .map((selectedLabelAndValue) => selectedLabelAndValue.label)
        .join(',');
      const answer_kbn = selectedLabelAndValues
        .map((selectedLabelAndValue) => selectedLabelAndValue.value)
        .join(',');
      return {
        apply_form_no: apply_form_no,
        answer,
        answer_etc: datas.etc ?? answer_etc,
        answer_kbn,
      };
    }
    // 04: ラジオボタン
    // 16: ドロップダウンリスト
    if (['04', '16'].includes(format)) {
      const data = values[apply_form_no] as RadioSchemaType;
      if (typeof data?.labelAndValues !== 'string' || data?.labelAndValues === '') {
        if (data?.etc) {
          return {
            apply_form_no: apply_form_no,
            answer: '',
            answer_etc: data.etc,
            answer_kbn: '',
          };
        }
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const labelAndValue = JSON.parse(data.labelAndValues) as { value: string; label: string };
      return {
        apply_form_no: apply_form_no,
        answer: labelAndValue.label,
        answer_etc: data.etc ?? answer_etc,
        answer_kbn: labelAndValue.value,
      };
    }
    // 06：日付選択
    if (['06'].includes(format)) {
      const data = values[apply_form_no] as unknown as string[];
      if (data == null) {
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const year = data[0];
      const month = data[1];
      const date = data[2];

      if (
        year !== '' &&
        month !== '' &&
        date !== '' &&
        !isValidDate(parseInt(year), parseInt(month), parseInt(date)) &&
        setHasErrorInSelectDate
      ) {
        setHasErrorInSelectDate(true);
        throw new Error('invalid date');
      }

      // YYYY/MM/DDの形で保存する。但し指定されていないものは除く
      const answer = year === '' && month === '' && date === '' ? '' : data.join('/');
      return {
        apply_form_no: apply_form_no,
        answer,
        answer_etc: answer_etc,
        answer_kbn: '',
      };
    }
    // 08:画像、09:動画、17:音源
    // 20:同意書
    if (['08', '09', '17', '20'].includes(format)) {
      const files = values[apply_form_no];
      // ファイルを選択していなくてもfileがFileインスタンスになるため、ファイルが選択されているかどうかを確認する
      if (!(files instanceof FileList && files.length > 0)) {
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      if (format === '20' && setHasErrorConsentFiles && files.length > 5) {
        setHasErrorConsentFiles(true);
        throw new Error('最大アップロード可能数は5つです。');
      }
      return {
        apply_form_no: apply_form_no,
        answer: {
          file: format === '20' ? files : (files.item(0) as File),
          type: format === '20' ? AnswerFileType.CONSENT : AnswerFileType.OTHER,
        },
        answer_etc: format !== '20' ? (files.item(0) as File).name : answer_etc,
        answer_kbn: '',
      };
    }
    // 54:メンバー情報
    if (['54'].includes(format)) {
      const consentAnswers = values[apply_form_no] as unknown as MembersAnswerInput[];
      const applyType = consentAnswers[0].apply_type;
      const memberNum = consentAnswers[0].member_num;
      const input1 = consentAnswers.map((data) => data.input1);
      const input2 = consentAnswers.map((data) => data.input2);
      const input3 = consentAnswers.map((data) => data.input3);
      const input4 = consentAnswers.map((data) => data.input4);
      const input5 = consentAnswers.map((data) => data.input5);
      const input6 = consentAnswers.map((data) => data.input6);
      const isInput1 = consentAnswers.some((data) => data.input1);
      const isInput2 = consentAnswers.some((data) => data.input2);
      const isInput3 = consentAnswers.some((data) => data.input3);
      const isInput4 = consentAnswers.some((data) => data.input4);
      const isInput5 = consentAnswers.some((data) => data.input5);
      const isInput6 = consentAnswers.some((data) => data.input6);
      const hasConsent = consentAnswers.some((data) => data.consent);

      const isSolo = applyType === 'Solo' || applyType === 'ソロ';
      // どれかは存在するはずなので全部からメンバー数を取得する
      const rows =
        input1.length > 0
          ? input1
          : input2.length > 0
            ? input2
            : input3.length > 0
              ? input3
              : input4.length > 0
                ? input4
                : input5.length > 0
                  ? input5
                  : input6;

      const membersApplyForm = applyForms.find((applyForms) => applyForms.format === '54');
      const [
        input1Exclude = false,
        input2Exclude = false,
        input3Exclude = false,
        input4Exclude = false,
        input5Exclude = false,
        input6Exclude = false,
      ] = membersApplyForm!.general1.split(',').map((v) => v !== '1');
      const members = rows.map((_, row) => {
        const member = [
          !input1Exclude ? (isInput1 ? input1[row] : '') : undefined,
          !input2Exclude ? (isGlobal ? '' : isInput2 ? input2[row] : '') : undefined,
          !input3Exclude ? (isInput3 ? input3[row] : '') : undefined,
          !input4Exclude ? (isInput4 ? input4[row] : '') : undefined,
          !input5Exclude ? (isInput5 ? input5[row] : '') : undefined,
          !input6Exclude ? (isInput6 ? input6[row] : '') : undefined,
        ]
          .filter((m) => m !== undefined)
          .join('、');
        return member;
      });
      if (
        isGlobal === false &&
        isSolo === false &&
        setHasErrorInMemberConsent &&
        members.length > 0 &&
        (consentAnswers[0].consent === false || !hasConsent)
      ) {
        setHasErrorInMemberConsent(true);
        throw new Error('dont check consent');
      }
      const answerHeader = `${isSolo ? `ソロ応募` : `グループ応募 ${memberNum && memberNum > 1 && !Number.isNaN(memberNum) ? memberNum : 2}名`}\n`;
      const membersAnswer = members.join('\n');
      const answer = isSolo ? `${answerHeader}` : `${answerHeader}${membersAnswer}`;
      return {
        apply_form_no,
        answer,
        answer_etc: isGlobal === false && consentAnswers[0]?.consent ? 'メンバー同意あり' : '',
        answer_kbn: '',
      };
    }
    // 57:カテゴリ
    if (['57'].includes(format)) {
      const datas = values[apply_form_no] as unknown as CategoryInputs;
      if (
        !datas ||
        !Array.isArray(datas.labelAndValues) ||
        datas.labelAndValues.length === 0 ||
        typeof datas.labelAndValues[0] !== 'string'
      ) {
        if (datas?.etc) {
          return {
            apply_form_no: apply_form_no,
            answer: '',
            answer_etc: datas.etc,
            answer_kbn: '',
          };
        }
        return {
          apply_form_no: apply_form_no,
          answer: '',
          answer_etc: '',
          answer_kbn: '',
        };
      }
      const selectedLabelAndValues = (datas.labelAndValues as unknown as string[]).map(
        (data) => JSON.parse(data.toString()) as { value: string; label: string }
      );

      const answer = selectedLabelAndValues
        .map((selectedLabelAndValue) => selectedLabelAndValue.label)
        .join(',');
      const answer_kbn = selectedLabelAndValues
        .map((selectedLabelAndValue) => selectedLabelAndValue.value)
        .join(',');
      return {
        apply_form_no: apply_form_no,
        answer,
        answer_etc: datas.etc ?? '',
        answer_kbn,
      };
    }
    const answer = values[apply_form_no];
    const answer_kbn = '';
    return {
      apply_form_no: apply_form_no,
      answer: answer == null ? '' : answer.toString(),
      answer_etc: answer_etc,
      answer_kbn: answer_kbn,
    };
  });
};

/**
 * AnswersをAuditionFormValuesSchemaInputに変換する
 */
export function transformAnswersToAuditionFormValues(
  answers: Answer[],
  applyForms: ApplyForm[],
  isGlobal: boolean
): AuditionFormValuesSchemaInput {
  if (applyForms.length <= 0) return {};
  return answers.reduce<AuditionFormValuesSchemaInput>((answers, answer) => {
    const apply_form_no = answer.apply_form_no as `${number}`;
    if (answer.answer === '') return answers;
    const format = applyForms.find(
      (form) => form.apply_form_no === Number(answer.apply_form_no)
    )?.format;
    if (!format) {
      return answers;
    }
    // 12：都道府県:国内
    // 52：希望会場
    if ((format === '12' && !isGlobal) || ['52'].includes(format)) {
      answers[apply_form_no] = answer.answer_kbn;
      return answers;
    }
    // 58: 参加可能会場
    if (['58'].includes(format)) {
      const labelAndValues = answer.answer_kbn.includes(',')
        ? answer.answer_kbn.split(',').map((v) => v)
        : [answer.answer_kbn];
      answers[apply_form_no] = {
        labelAndValues: labelAndValues,
      };
      return answers;
    }
    // 03: リストボックス
    if (['03'].includes(format)) {
      const labels = answer.answer.split(',');
      const etc = answer.answer_etc;
      const valueAndLabels: { value: string; label: string }[] = answer.answer_kbn
        .split(',')
        .map((v, i) => ({ value: v, label: labels[i] }));

      answers[apply_form_no] = {
        labelAndValues: valueAndLabels.map((v) => JSON.stringify(v)),
        etc: etc,
      };
      return answers;
    }
    // 04: チェックボックス
    // 16: ドロップダウンリスト
    if (['04', '16'].includes(format)) {
      const { answer: label, answer_kbn: value, answer_etc: etc } = answer;

      const valueAndLabel: { value: string; label: string } = {
        value,
        label,
      };
      answers[apply_form_no] = {
        labelAndValues: JSON.stringify(valueAndLabel),
        etc: etc,
      };
      return answers;
    }
    // 06：日付選択
    if (['06'].includes(format)) {
      answers[apply_form_no] = answer.answer.split('/');
      return answers;
    }
    // 54: メンバー情報
    if (['54'].includes(format)) {
      const answerHeader = answer.answer.substring(0, answer.answer.indexOf('\n'));
      const isSolo = answerHeader.includes('ソロ') || answerHeader.includes('Solo');
      const members = answer.answer.split('\n').slice(1); //先頭行除外
      const consentAnswers = members.map((m) => m.split('、'));
      console.debug(consentAnswers);
      // answerには結合された値が入っているので、分割して配列にする
      const membersApplyForm = applyForms.find((applyForms) => applyForms.format === '54');
      if (membersApplyForm == null) return answers;
      const [
        input1Exclude,
        input2Exclude,
        input3Exclude,
        input4Exclude,
        input5Exclude,
        input6Exclude,
      ] = membersApplyForm.general1.split(',').map((v) => v !== '1');
      answers[apply_form_no] = members.map<MembersAnswerInput>((_, memberIndex) =>
        (
          [
            'input1',
            input2Exclude ? undefined : 'input2',
            input3Exclude ? undefined : 'input3',
            input4Exclude ? undefined : 'input4',
            input5Exclude ? undefined : 'input5',
            input6Exclude ? undefined : 'input6',
            memberIndex === 0 ? 'apply_type' : undefined,
            memberIndex === 0 ? 'member_num' : undefined,
            answer.answer_etc && memberIndex === 0 && !isGlobal ? 'consent' : undefined,
          ] as const
        )
          .filter((name) => name !== undefined)
          .reduce((acc, name, index) => {
            if (name === 'apply_type') {
              acc[name] = isGlobal ? (isSolo ? 'Solo' : 'Group') : isSolo ? 'ソロ' : 'グループ';
            } else if (name === 'member_num') {
              acc[name] = isSolo
                ? 2
                : parseInt(
                    answerHeader.substring(
                      answerHeader.indexOf(' ') + 1,
                      answerHeader.indexOf('名')
                    )
                  );
            } else if (name === 'consent') {
              acc[name] = answer.answer_etc !== '' ? true : false;
            } else {
              acc[name] = consentAnswers[memberIndex][index];
            }
            return acc;
          }, {} as MembersAnswerInput)
      );
      return answers;
    }
    // 57: カテゴリ
    if (['57'].includes(format)) {
      const labels = answer.answer.split(',');
      const etc = answer.answer_etc;
      const valueAndLabels: { value: string; label: string }[] = answer.answer_kbn
        .split(',')
        .map((v, i) => ({ value: v, label: labels[i] }));

      answers[apply_form_no] = {
        labelAndValues: valueAndLabels.map((v) => JSON.stringify(v)),
        etc: etc,
      };
      return answers;
    }

    answers[apply_form_no] = answer.answer;
    return answers;
  }, {});
}

export const useAuditionForm = ({
  applyForms,
  prefectures,
  preferred_venue_list,
  isAutoSave = false,
  values,
}: {
  applyForms: ApplyForm[];
  prefectures: { value: string; label: string }[];
  preferred_venue_list: { value: string; label: string }[];
  isAutoSave?: boolean;
  values?: AuditionFormValuesSchemaInput;
}) => {
  const { isGlobal } = useContext(AppContext);
  const cache = useCachedValues(applyForms, isAutoSave);

  const schema = createAuditionFormValuesSchema(applyForms, isGlobal);
  const props = useForm<AuditionFormValuesSchemaInput>({
    resolver: zodResolver(schema),
    // values: defaultValues,
    ...(isAutoSave
      ? {
          values: { ...cache, ...values },
          resetOptions: {
            keepDirtyValues: true, // keep dirty fields unchanged, but update defaultValues
          },
        }
      : { defaultValues: values }),
  });

  useAutoSave({
    applyForms,
    prefectures,
    preferred_venue_list,
    values: props.watch(),
    enable: isAutoSave,
  });

  return props;
};

function useCachedValues(applyForms: ApplyForm[], enable = true) {
  const { audition_id: auditionId, examination_no: examinationNo } =
    useContext(AuditionFormRouteContext);
  const { isGlobal } = useContext(AppContext);

  const [values, setValues] = useState<AuditionFormValuesSchemaInput>({});
  useEffect(() => {
    if (!enable) return;
    console.log('ローカルストレージからデータを取得: useEffect', auditionId, examinationNo);
    const answers = getAnswers(auditionId, examinationNo);
    if (!answers) return;
    setValues(transformAnswersToAuditionFormValues(answers, applyForms, isGlobal));
  }, [auditionId, examinationNo, applyForms, enable]);

  return values;
}

function useAutoSave({
  values,
  applyForms,
  prefectures,
  preferred_venue_list,
  enable = true,
}: {
  values: AuditionFormValuesSchemaInput;
  applyForms: ApplyForm[];
  prefectures: { value: string; label: string }[];
  preferred_venue_list: { value: string; label: string }[];
  enable?: boolean;
}) {
  const { audition_id: auditionId, examination_no: examinationNo } =
    useContext(AuditionFormRouteContext);
  const { isGlobal } = useContext(AppContext);

  useEffect(() => {
    if (!enable) return;
    console.log('フォーカスアウト時にデータを保存: useEffect');
    const formElement = document.querySelector('form');
    if (!formElement) return;

    const handleFocusOut = () => {
      console.debug('フォーカスアウト時にデータを保存: handleFocusOut');
      const data = transformAuditionFormInputToAuditionFormValues(
        applyForms,
        prefectures,
        preferred_venue_list,
        values,
        isGlobal
      );
      // ファイルは保存しない
      const saveableData = data.map(({ answer, answer_kbn, answer_etc, ...props }) => {
        return {
          answer: typeof answer !== 'string' ? '' : answer,
          answer_kbn: typeof answer_kbn !== 'string' ? '' : answer_kbn,
          answer_etc: typeof answer_etc !== 'string' ? '' : answer_etc,
          ...props,
        };
      });
      setAnswers(saveableData, auditionId, examinationNo);
    };

    // 各フォームのフォーカスアウト&リロードを検知
    window.addEventListener('beforeunload', handleFocusOut);
    formElement.addEventListener('focusout', handleFocusOut);
    return () => {
      window.removeEventListener('beforeunload', handleFocusOut);
      formElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [auditionId, examinationNo, values, enable]);
}

function isValidDate(year: number, month: number, date: number): boolean {
  const dateObj = new Date(year, month - 1, date);

  return (
    dateObj.getFullYear() === year && dateObj.getMonth() === month - 1 && dateObj.getDate() === date
  );
}
